import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalNames, PROJECT } from '@dev-fast/types';
import { Store } from '@ngxs/store';
import { Socket } from 'ngx-socket-io';
import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, filter, fromEvent, map, startWith, tap, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EnvironmentService } from '@app/core/environment-service';
import { CloseAllModals, ModalsState, OpenModal } from '@app/core/state/modals';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  readonly #store = inject(Store);
  readonly #environmentService = inject(EnvironmentService);
  readonly #document = inject(DOCUMENT);
  readonly #isMarket = this.#environmentService.isProject({ name: PROJECT.MARKET });
  readonly #isDevelop = !this.#environmentService.getEnvironment().production;

  readonly #connectTimeout = 60 * 1000; // 1 минута
  #reconnectSubscription: Subscription | null = null;
  #initialConnectSubscription: Subscription | null = null;
  #isMonitoring = false;

  readonly #visibilityChange$: Observable<boolean> = fromEvent(this.#document, 'visibilitychange').pipe(
    startWith(true),
    map(() => this.#document.visibilityState !== 'hidden'),
  );

  readonly socket = inject(Socket);

  readonly #isConnected$ = new BehaviorSubject<boolean>(false);
  readonly isConnected$ = this.#isConnected$.asObservable();
  readonly isConnected = toSignal(this.isConnected$, { requireSync: true });

  monitorSocket(): void {
    if (this.#isMonitoring) {
      return;
    }
    this.#isMonitoring = true;
    // Подписка на событие отключения
    this.socket.on('disconnect', this.#handleDisconnect);
    // Подписка на событие успешного подключения
    this.socket.on('connect', this.#handleConnect);
    // Подписка на попытки переподключения
    this.socket.on('reconnect_attempt', this.#logReconnectAttempt);
    // Подписка на ошибки подключения
    this.socket.on('connect_error', this.#logConnectError);
    if (!this.socket.ioSocket.active) {
      this.socket.connect();
    }
    // Запуск таймера на первое подключение
    this.#startInitialConnectTimer();
  }

  #handleDisconnect = (): void => {
    this.#isConnected$.next(false);
    this.#visibilityChange$
      .pipe(
        filter((isVisible) => isVisible),
        tap(() => {
          this.#showLog('Socket disconnected');
          this.#startReconnectTimer();
        }),
        takeUntil(this.socket.fromEvent('connect')),
      )
      .subscribe();
  };

  #handleConnect = (): void => {
    this.#isConnected$.next(true);
    this.#showLog('Socket connected');
    this.#clearReconnectTimer();
    this.#clearInitialConnectTimer();
  };

  #logReconnectAttempt = (attemptNumber: number): void => {
    this.#showLog(`Попытка переподключения №${attemptNumber}`);
  };

  #logConnectError = (error: any): void => {
    this.#showLog(`Ошибка подключения к сокету: ${error.message}`);
  };

  #startInitialConnectTimer(): void {
    if (this.socket.ioSocket.connected) {
      this.#isConnected$.next(true);
      return; // Если соединение уже установлено, ничего не делаем
    }
    this.#initialConnectSubscription = timer(this.#connectTimeout)
      .pipe(
        tap(() => {
          this.#showLog(`Не удалось подключиться к сокету в течение ${this.#connectTimeout / 1000} секунд.`);
          // Логика при неудачном первом подключении
          this.#handleFailedReconnect();
        }),
        takeUntil(this.socket.fromEvent('connect')),
      ) // Останавливаем таймер, если соединение успешно
      .subscribe();
  }

  #clearInitialConnectTimer = (): void => {
    if (this.#initialConnectSubscription) {
      this.#initialConnectSubscription.unsubscribe();
      this.#initialConnectSubscription = null;
    }
  };

  #startReconnectTimer(): void {
    if (this.#reconnectSubscription) {
      return; // Если таймер уже запущен, ничего не делаем
    }

    // Таймер для ожидания восстановления соединения
    this.#reconnectSubscription = timer(this.#connectTimeout)
      .pipe(
        tap(() => {
          this.#handleFailedReconnect();
        }),
        takeUntil(this.socket.fromEvent('connect')),
      )
      .subscribe();
  }

  #clearReconnectTimer(): void {
    if (this.#reconnectSubscription) {
      this.#reconnectSubscription.unsubscribe();
      this.#reconnectSubscription = null;
    }
  }

  #handleFailedReconnect(): void {
    // Логика на случай, если соединение не восстановилось
    this.#openTechnicalWorkModal();
  }

  #openTechnicalWorkModal(): void {
    const activeModals = this.#store.selectSnapshot(ModalsState.activeModals);
    if (activeModals.includes(ModalNames.TECHNICAL_WORK)) {
      return;
    }
    if (!this.#isDevelop && this.#isMarket) {
      if (activeModals.length) {
        this.#store.dispatch(new CloseAllModals());
      }
      this.#store.dispatch(new OpenModal(ModalNames.TECHNICAL_WORK));
    }
  }

  #showLog(message: string): void {
    if (this.#isDevelop) {
      // eslint-disable-next-line no-console
      console.log(message);
    }
  }
}
